import type { MarginProps } from "styled-system";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import {
  type ButtonHTMLAttributes,
  type ClassAttributes,
  type ForwardedRef,
  forwardRef,
} from "react";
import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";
import { TooltipV3 } from "src/components/TooltipV3/TooltipV3.tsx";
import type { TooltipProps } from "@radix-ui/react-tooltip";
import { css } from "@emotion/react";

type Skin = "info" | "danger" | "success";

const getButtonColorStyles = ({
  skin,
  disabled,
  theme,
}: {
  theme: ExactTheme;
  skin: Skin;
  disabled?: boolean;
}) => {
  const { colorsV2 } = theme;

  if (disabled) {
    return {
      background: "transparent",
      color: colorsV2.grey[600],
    };
  }

  const colorName = (
    {
      info: "blue",
      success: "green",
      danger: "red",
    } as const
  )[skin];

  return {
    transition: "150ms",

    background: "transparent",
    color: colorsV2[colorName][500],
    ":hover": {
      background: colorsV2[colorName][50],
    },
    ":active": {
      color: colorsV2[colorName][800],
      background: colorsV2[colorName][100],
    },
    ":focus": {
      outline: "none",
    },
    ":focus-visible": {
      color: "white",
      background: colorsV2[colorName][700],
      outline: `2px solid ${colorsV2[colorName][100]}`,
    },
  };
};

type Theme = Omit<ExactTheme, "buttons">;

export interface IconButton2Props
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    ClassAttributes<HTMLButtonElement>,
    MarginProps<Theme> {
  disabled?: boolean;
  isLoading?: boolean;
  bordered?: boolean;
  inline?: boolean;
  helperText?: string;
  tooltipProps?: TooltipProps;
  skin: Skin;
}

const buttonStyles =
  ({
    skin,
    disabled,
    bordered,
    inline,
  }: {
    disabled?: boolean;
    inline?: boolean;
    bordered?: boolean;
    skin: Skin;
  }) =>
  (theme: ExactTheme) => {
    const styles = [];
    styles.push(css`
      cursor: ${disabled ? "not-allowed" : "pointer"};

      position: relative;
      flex-shrink: 0;

      font-size: ${theme.fontSizesV2.body};
      line-height: 1.25;
      background: transparent;
      border: none;
      border-radius: ${theme.borderRadiusV2.primary};
      :focus {
        outline: none;
      }

      ${getButtonColorStyles({
        skin,
        disabled,
        theme,
      })}
    `);

    if (bordered) {
      const colorName = (
        {
          danger: "red",
          info: "blue",
          success: "green",
        } as const
      )[skin];
      styles.push(css`
        border: 1px solid ${theme.colorsV2[colorName][100]};
      `);
    }

    if (inline) {
      styles.push(css`
        display: inline-flex;
      `);
    } else {
      styles.push(css`
        display: flex;
        justify-content: center;
        align-items: center;
        width: 25px;
        height: 25px;
      `);
    }
    return styles;
  };

export const IconButton2 = forwardRef(
  (props: IconButton2Props, ref: ForwardedRef<HTMLButtonElement>) => {
    const {
      isLoading = false,
      bordered = false,
      skin = "info",
      inline = false,
      disabled,
      helperText,
      children,
      tooltipProps,
      // Ohne Angabe ist ein <button> im Formular ein Absende-Knopf: der „+“-Knopf der
      // Zuordnung speicherte so die Buchung. Absenden muss ausdrücklich angefordert werden.
      type = "button",
      ...rest
    } = props;
    return (
      <Skeleton isLoading={isLoading}>
        <TooltipV3
          contentProps={{
            sideOffset: 5,
            alignOffset: 5,
          }}
          disableHoverableContent={true}
          enabled={!!helperText && !disabled}
          content={helperText}
          {...tooltipProps}
        >
          <button
            type={type}
            disabled={disabled}
            css={buttonStyles({
              skin,
              inline,
              bordered,
              disabled,
            })}
            ref={ref}
            {...rest}
          >
            {children}
          </button>
        </TooltipV3>
      </Skeleton>
    );
  },
);
